import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../config/firebase';
import { collection, getDocs, doc, deleteDoc, addDoc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import API_BASE_URL from '../config/api';

const AdminPartnersManagementPage = ({ darkMode }) => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [approvedStudents, setApprovedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [authReady, setAuthReady] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showReferrals, setShowReferrals] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState('');
  const [showEditPartner, setShowEditPartner] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [editForm, setEditForm] = useState({
    partnerName: '',
    phoneNumber: '',
    email: '',
    institutionName: '',
    address: '',
    paymentAccount: {
      hasUpi: false,
      hasBank: false,
      upiId: '',
      bankAccount: {
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        accountHolderName: ''
      }
    }
  });

  // Referral stages with SVG icons
  const referralStages = [
    { 
      id: 'referred', 
      label: 'Referred', 
      icon: () => (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    { 
      id: 'seatBooked', 
      label: 'Seat Booked', 
      icon: () => (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    { 
      id: 'testProvided', 
      label: 'Test Provided', 
      icon: () => (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    { 
      id: 'invitationLetter', 
      label: 'Invitation Letter Issued', 
      icon: () => (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    },
    { 
      id: 'admissionConfirmed', 
      label: 'Admission Letter Confirmed', 
      icon: () => (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
        </svg>
      )
    },
    { 
      id: 'visaApproved', 
      label: 'Visa Approved', 
      icon: () => (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    { 
      id: 'flightBooked', 
      label: 'Flight Booked & Departed', 
      icon: () => (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
        </svg>
      )
    }
  ];

  // Fetch partners from Firebase with retry mechanism
  const fetchPartnersFromFirebase = async (retryCount = 0) => {
    try {
      console.log(`🔄 Attempting to fetch partners from Firebase (attempt ${retryCount + 1})`);
      const partnersRef = collection(db, 'partners');
      const snapshot = await getDocs(partnersRef);
      
      const firebasePartners = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        firebasePartners.push({
          id: doc.id,
          name: data.partnerName,
          phone: data.phoneNumber,
          email: data.email,
          institution: data.institutionName || '',
          address: data.address,
          registeredAt: data.registeredAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          totalReferrals: data.totalReferrals || 0,
          activeReferrals: data.activeReferrals || 0,
          status: data.status || 'active',
          partnerID: data.partnerID || null,
          paymentAccount: data.paymentAccount || null
        });
      });

      // Sort partners by ID number for stable ordering
      firebasePartners.sort((a, b) => {
        if (a.partnerID && b.partnerID) {
          const aID = parseInt(a.partnerID.replace(/\D/g, '')) || 0;
          const bID = parseInt(b.partnerID.replace(/\D/g, '')) || 0;
          return aID - bID;
        }
        
        if (!a.partnerID && !b.partnerID) {
          return a.phone.localeCompare(b.phone);
        }
        
        return a.partnerID ? -1 : 1;
      });
      
      setPartners(firebasePartners);
      console.log('✅ Loaded partners from Firebase (sorted by ID for stable order):', firebasePartners);
      return true;
      
    } catch (error) {
      console.error(`🔒 Firebase permissions denied for partners (attempt ${retryCount + 1}):`, error);
      
      // Retry up to 2 times with increasing delays
      if (retryCount < 2) {
        console.log(`⏳ Retrying in ${(retryCount + 1) * 2} seconds...`);
        await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 2000));
        return fetchPartnersFromFirebase(retryCount + 1);
      }
      
      console.error('🚨 All retry attempts failed - partners data cannot be loaded due to Firebase permissions');
      setPartners([]);
      setError('Unable to load partners data. Please check your permissions or contact support.');
      return false;
    }
  };

  // Fetch referrals from Firebase (no API fallback - referrals API doesn't exist)
  const fetchReferralsFromFirebase = async () => {
    try {
      const allReferrals = [];
      
      // Get all partners first
      const partnersRef = collection(db, 'partners');
      const partnersSnapshot = await getDocs(partnersRef);
      
      // For each partner, get their referrals
      for (const partnerDoc of partnersSnapshot.docs) {
        const partnerId = partnerDoc.id;
        const partnerData = partnerDoc.data();
        const referralsRef = collection(db, 'partners', partnerId, 'referrals');
        const referralsSnapshot = await getDocs(referralsRef);
        
        referralsSnapshot.forEach((doc) => {
          const referralData = doc.data();
          allReferrals.push({
            id: doc.id,
            partnerId: partnerId,
            partnerName: partnerData.partnerName,
            ...referralData
          });
        });
      }
      
      setReferrals(allReferrals);
      console.log('✅ Loaded referrals from Firebase:', allReferrals);
      return true;
    } catch (error) {
      console.error('🔒 Firebase permissions denied for referrals - no API fallback available');
      console.error('Referrals data cannot be loaded due to Firebase permissions:', error);
      setReferrals([]);
      return false;
    }
  };

  // Wait for authentication to be ready before fetching data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.email === 'doctorworldconsultancy@gmail.com') {
        console.log('🔐 Admin authentication confirmed, ready to fetch data');
        setAuthReady(true);
      } else {
        console.log('🚫 No valid admin authentication');
        setAuthReady(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authReady) {
      console.log('⏳ Waiting for authentication to be ready...');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        console.log('🔄 Loading partners management data...');
        
        // Fetch partners with Firebase/API fallback
        const partnersLoaded = await fetchPartnersFromFirebase();
        
        // Fetch referrals with Firebase/API fallback
        const referralsLoaded = await fetchReferralsFromFirebase();
        
        // Fetch approved students with Firebase/API fallback
        await fetchApprovedStudents();
        
        console.log('✅ Partners management data loaded successfully');
      } catch (error) {
        console.error('Error fetching partners management data:', error);
        setError('Failed to load data: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authReady]);

  const fetchApprovedStudents = async () => {
    try {
      // Try Firebase first
      const studentsRef = collection(db, 'students');
      const snapshot = await getDocs(studentsRef);
      
      const approvedStudentsList = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        // Only include students with 'Approved' status
        if (data.status === 'Approved') {
          approvedStudentsList.push({
            id: data.applicationId || data._id || doc.id, // Use applicationId first (contains OV ID), then fallback to document ID
            name: data.fullName || data.name,
            email: data.email,
            phone: data.phoneNumber || data.phone,
            desiredCollege: data.desiredCollege || data.university,
            status: data.status
          });
        }
      });
      
      console.log('✅ Loaded approved students from Firebase:', approvedStudentsList);
      setApprovedStudents(approvedStudentsList);
    } catch (error) {
      console.error('🔒 Firebase permissions denied for approved students - falling back to API');
      
      try {
        // Fallback to API (use the same students API as admin dashboard)
        const response = await axios.get(`${API_BASE_URL}/api/admin/students`);
        
        if (response.data.success && response.data.students) {
          const approvedStudentsList = response.data.students
            .filter(student => student.status === 'Approved')
            .map(student => ({
              id: student.applicationId || student._id,
              name: student.fullName || student.name,
              email: student.email,
              phone: student.phoneNumber || student.phone,
              desiredCollege: student.desiredCollege || student.university,
              status: student.status
            }));
          
          console.log('✅ Loaded approved students from API:', approvedStudentsList);
          setApprovedStudents(approvedStudentsList);
        }
      } catch (apiError) {
        console.error('Error fetching approved students from API:', apiError);
        setApprovedStudents([]);
      }
    }
  };

  const handleDeleteReferral = async (referralId) => {
    if (window.confirm('Are you sure you want to delete this referral?')) {
      try {
        const referral = referrals.find(r => r.id === referralId);
        if (!referral) return;

        // Delete from Firebase
        const referralRef = doc(db, 'partners', referral.partnerId, 'referrals', referralId);
        await deleteDoc(referralRef);
        console.log('Referral deleted from Firebase');
        
        // Update local state
        setReferrals(prev => prev.filter(r => r.id !== referralId));
      } catch (error) {
        console.error('Error deleting referral:', error);
        setError('Failed to delete referral: ' + error.message);
      }
    }
  };

  const handleAddStudent = async (studentId) => {
    try {
      const student = approvedStudents.find(s => s.id === studentId);
      if (!student) return;

      // Check for duplicate referral across ALL partners (not just current partner)
      const existingReferral = referrals.find(ref => ref.studentEmail === student.email);

      if (existingReferral) {
        // Don't allow adding - just return without any notification
        return;
      }

      const newReferral = {
        partnerId: selectedPartner.id,
        partnerName: selectedPartner.name,
        studentName: student.name,
        studentPhone: student.phone,
        studentEmail: student.email,
        university: student.desiredCollege,
        referredAt: new Date().toISOString(),
        stages: {
          referred: true,
          seatBooked: false,
          testProvided: false,
          invitationLetter: false,
          admissionConfirmed: false,
          visaApproved: false,
          flightBooked: false
        }
      };

      // Add to Firebase
      const referralsRef = collection(db, 'partners', selectedPartner.id, 'referrals');
      const docRef = await addDoc(referralsRef, newReferral);
      newReferral.id = docRef.id;
      console.log('Referral added to Firebase with ID:', docRef.id);

      // Update local state immediately
      setReferrals(prev => [...prev, newReferral]);
      setShowAddStudent(false);
      setStudentSearchTerm('');
      
    } catch (error) {
      console.error('Error adding student referral:', error);
      setError('Failed to add student referral: ' + error.message);
    }
  };

  const handleStageToggle = async (referralId, stageId) => {
    try {
      const referral = referrals.find(r => r.id === referralId);
      if (!referral) return;

      const currentStageIndex = referralStages.findIndex(stage => stage.id === stageId);
      const newStageValue = !referral.stages[stageId];
      
      // If trying to enable a stage, check if all previous stages are completed
      if (newStageValue) {
        for (let i = 0; i < currentStageIndex; i++) {
          const previousStage = referralStages[i];
          if (!referral.stages[previousStage.id]) {
            // Don't allow selecting this stage - just return without any notification
            return;
          }
        }
      }
      
      // If disabling a stage, disable all subsequent stages as well
      let updatedStages = { ...referral.stages };
      if (!newStageValue) {
        // Disable current stage and all following stages
        for (let i = currentStageIndex; i < referralStages.length; i++) {
          updatedStages[referralStages[i].id] = false;
        }
      } else {
        // Just enable the current stage
        updatedStages[stageId] = true;
      }
      
      // Prepare update data
      const updateData = { stages: updatedStages };
      
      // If flight is being marked as booked, add timestamp
      if (stageId === 'flightBooked' && newStageValue) {
        updateData.flightBookedAt = new Date().toISOString();
      }
      
      // Update local state immediately for better UX
      setReferrals(prev => prev.map(r => {
        if (r.id === referralId) {
          return {
            ...r,
            stages: updatedStages,
            flightBookedAt: updateData.flightBookedAt || r.flightBookedAt
          };
        }
        return r;
      }));

      // Update in Firebase
      const referralRef = doc(db, 'partners', referral.partnerId, 'referrals', referralId);
      await updateDoc(referralRef, updateData);
      
      console.log('Stages updated in Firebase:', updateData);
    } catch (error) {
      console.error('Error updating stage:', error);
      setError('Failed to update stage: ' + error.message);
      
      // Revert local state on error
      setReferrals(prev => prev.map(r => {
        if (r.id === referralId) {
          return {
            ...r,
            stages: {
              ...r.stages,
              [stageId]: !r.stages[stageId] // Revert
            }
          };
        }
        return r;
      }));
    }
  };

  // Partner management functions
  const handleDeletePartner = async (partnerId) => {
    try {
      // Delete partner from Firebase
      await deleteDoc(doc(db, 'partners', partnerId));
      console.log('Partner deleted from Firebase');
      
      // Remove from local state
      setPartners(prev => prev.filter(p => p.id !== partnerId));
      setReferrals(prev => prev.filter(r => r.partnerId !== partnerId));
      
      setShowDeleteConfirm(false);
      setPartnerToDelete(null);
    } catch (error) {
      console.error('Error deleting partner:', error);
      setError('Failed to delete partner: ' + error.message);
    }
  };

  // Edit partner functions
  const handleEditPartner = (partner) => {
    setEditingPartner(partner);
    setError(''); // Clear any existing errors
    setSuccessMessage(''); // Clear any existing success messages
    const paymentAccount = partner.paymentAccount || {};
    setEditForm({
      partnerName: partner.name,
      phoneNumber: partner.phone,
      email: partner.email,
      institutionName: partner.institution || '',
      address: partner.address,
      paymentAccount: {
        hasUpi: paymentAccount.hasUpi || (paymentAccount.upiId ? true : false),
        hasBank: paymentAccount.hasBank || (paymentAccount.bankAccount?.accountNumber ? true : false),
        upiId: paymentAccount.upiId || '',
        bankAccount: {
          accountNumber: paymentAccount.bankAccount?.accountNumber || '',
          ifscCode: paymentAccount.bankAccount?.ifscCode || '',
          bankName: paymentAccount.bankAccount?.bankName || '',
          accountHolderName: paymentAccount.bankAccount?.accountHolderName || ''
        }
      }
    });
    setShowEditPartner(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('bankAccount.')) {
      const fieldName = name.replace('bankAccount.', '');
      setEditForm(prev => ({
        ...prev,
        paymentAccount: {
          ...prev.paymentAccount,
          bankAccount: {
            ...prev.paymentAccount.bankAccount,
            [fieldName]: value
          }
        }
      }));
    } else if (name.startsWith('paymentAccount.')) {
      const fieldName = name.replace('paymentAccount.', '');
      setEditForm(prev => ({
        ...prev,
        paymentAccount: {
          ...prev.paymentAccount,
          [fieldName]: value
        }
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePaymentTypeChange = (type, enabled) => {
    setEditForm(prev => ({
      ...prev,
      paymentAccount: {
        ...prev.paymentAccount,
        [type]: enabled,
        // Clear data when disabling a payment type
        ...(type === 'hasUpi' && !enabled && { upiId: '' }),
        ...(type === 'hasBank' && !enabled && { 
          bankAccount: {
            accountNumber: '',
            ifscCode: '',
            bankName: '',
            accountHolderName: ''
          }
        })
      }
    }));
  };

  const handleSavePartner = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      // Validate required fields
      if (!editForm.partnerName.trim()) {
        setError('Partner name is required');
        setLoading(false);
        return;
      }
      
      if (!editForm.phoneNumber.trim() || editForm.phoneNumber.length !== 10) {
        setError('Valid 10-digit phone number is required');
        setLoading(false);
        return;
      }
      
      if (!editForm.email.trim()) {
        setError('Email address is required');
        setLoading(false);
        return;
      }
      
      if (!editForm.address.trim()) {
        setError('Address is required');
        setLoading(false);
        return;
      }

      // Validate payment details if enabled
      if (editForm.paymentAccount.hasUpi && !editForm.paymentAccount.upiId.trim()) {
        setError('UPI ID is required when UPI payment is enabled');
        setLoading(false);
        return;
      }

      if (editForm.paymentAccount.hasBank) {
        const { accountNumber, ifscCode, bankName, accountHolderName } = editForm.paymentAccount.bankAccount;
        if (!accountNumber.trim() || !ifscCode.trim() || !bankName.trim() || !accountHolderName.trim()) {
          setError('All bank account fields are required when bank payment is enabled');
          setLoading(false);
          return;
        }
      }
      
      // Prepare payment account data
      const paymentAccount = {
        hasUpi: editForm.paymentAccount.hasUpi,
        hasBank: editForm.paymentAccount.hasBank,
        upiId: editForm.paymentAccount.hasUpi ? editForm.paymentAccount.upiId : '',
        bankAccount: editForm.paymentAccount.hasBank ? editForm.paymentAccount.bankAccount : {
          accountNumber: '',
          ifscCode: '',
          bankName: '',
          accountHolderName: ''
        }
      };

      // Only save payment account if at least one method is enabled
      const hasPaymentInfo = editForm.paymentAccount.hasUpi || editForm.paymentAccount.hasBank;
      
      // Update partner in Firebase
      const partnerRef = doc(db, 'partners', editingPartner.id);
      await updateDoc(partnerRef, {
        partnerName: editForm.partnerName,
        phoneNumber: editForm.phoneNumber,
        email: editForm.email,
        institutionName: editForm.institutionName,
        address: editForm.address,
        paymentAccount: hasPaymentInfo ? paymentAccount : null,
        updatedAt: serverTimestamp()
      });

      // Update local state
      setPartners(prev => prev.map(partner => 
        partner.id === editingPartner.id 
          ? {
              ...partner,
              name: editForm.partnerName,
              phone: editForm.phoneNumber,
              email: editForm.email,
              institution: editForm.institutionName,
              address: editForm.address,
              paymentAccount: hasPaymentInfo ? paymentAccount : null
            }
          : partner
      ));

      setShowEditPartner(false);
      setEditingPartner(null);
      setError('');
      setSuccessMessage('Partner updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Error updating partner:', error);
      setError('Failed to update partner: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Real-time Firebase listeners disabled due to permission restrictions
  // Instead, we rely on the initial data fetch and manual refresh when needed
  /*
  useEffect(() => {
    // Listen for partners changes
    const unsubscribePartners = onSnapshot(collection(db, 'partners'), (snapshot) => {
      const firebasePartners = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        firebasePartners.push({
          id: doc.id,
          name: data.partnerName,
          phone: data.phoneNumber,
          email: data.email,
          institution: data.institutionName || '',
          address: data.address,
          registeredAt: data.registeredAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          totalReferrals: data.totalReferrals || 0,
          activeReferrals: data.activeReferrals || 0,
          status: data.status || 'active',
          partnerID: data.partnerID || null, // Include OV ID if available
          paymentAccount: data.paymentAccount || null
        });
      });

      // Sort partners by ID number for stable ordering (prevents shuffling after edits)
      firebasePartners.sort((a, b) => {
        // Primary sort: by partnerID (OV ID) if both have it
        if (a.partnerID && b.partnerID) {
          const aID = parseInt(a.partnerID.replace(/\D/g, '')) || 0;
          const bID = parseInt(b.partnerID.replace(/\D/g, '')) || 0;
          return aID - bID;
        }
        
        // Secondary sort: by phone number if partnerID is missing
        if (!a.partnerID && !b.partnerID) {
          return a.phone.localeCompare(b.phone);
        }
        
        // Prioritize partners with partnerID over those without
        return a.partnerID ? -1 : 1;
      });

      setPartners(firebasePartners);
      console.log('Real-time partners update (sorted by ID for stable order):', firebasePartners);
    });

    // Listen for referrals changes across all partners
    const unsubscribeReferrals = onSnapshot(collection(db, 'partners'), async (partnersSnapshot) => {
      const allReferrals = [];
      
      for (const partnerDoc of partnersSnapshot.docs) {
        const partnerId = partnerDoc.id;
        const partnerData = partnerDoc.data();
        
        try {
          const referralsRef = collection(db, 'partners', partnerId, 'referrals');
          const referralsSnapshot = await getDocs(referralsRef);
          
          referralsSnapshot.forEach((doc) => {
            const referralData = doc.data();
            allReferrals.push({
              id: doc.id,
              partnerId: partnerId,
              partnerName: partnerData.partnerName,
              ...referralData
            });
          });
        } catch (error) {
          console.error(`Error fetching referrals for partner ${partnerId}:`, error);
        }
      }
      
      setReferrals(allReferrals);
      console.log('Real-time referrals update:', allReferrals);
    });

    return () => {
      unsubscribePartners();
      unsubscribeReferrals();
    };
  }, []);
  */

  const filteredPartners = partners.filter(partner =>
    partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    partner.phone.includes(searchTerm) ||
    partner.institution.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPartnerReferrals = (partnerId) => {
    return referrals.filter(referral => referral.partnerId === partnerId);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStageProgress = (stages) => {
    const totalStages = referralStages.length;
    const completedStages = referralStages.filter(stage => stages[stage.id]).length;
    return Math.round((completedStages / totalStages) * 100);
  };

  const getPartnerAverageProgress = (partnerId) => {
    const partnerReferrals = getPartnerReferrals(partnerId);
    if (partnerReferrals.length === 0) return 0;
    
    const totalProgress = partnerReferrals.reduce((sum, referral) => {
      return sum + getStageProgress(referral.stages);
    }, 0);
    
    return Math.round(totalProgress / partnerReferrals.length);
  };

  // Circular Progress Component
  const CircularProgress = ({ percentage, size = 40 }) => {
    const radius = (size - 4) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    // Get color based on percentage
    const getProgressColor = (percent) => {
      if (percent <= 15) return '#ef4444'; // Red
      if (percent <= 30) return '#f97316'; // Orange
      if (percent <= 45) return '#f59e0b'; // Amber
      if (percent <= 60) return '#eab308'; // Yellow
      if (percent <= 75) return '#84cc16'; // Lime
      if (percent <= 90) return '#22c55e'; // Green
      return '#10b981'; // Emerald (100%)
    };

    const progressColor = getProgressColor(percentage);

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          className="transform -rotate-90"
          width={size}
          height={size}
        >
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={darkMode ? '#374151' : '#e5e7eb'}
            strokeWidth="3"
            fill="transparent"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={progressColor}
            strokeWidth="3"
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500 ease-in-out"
          />
        </svg>
        {/* Percentage text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span 
            className="text-xs font-bold transition-colors duration-500"
            style={{ color: progressColor }}
          >
            {percentage}%
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        darkMode 
          ? 'bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900' 
          : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto"></div>
          <p className={`mt-4 text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Loading partners data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen relative overflow-hidden ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900' 
        : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
    }`}>
      
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="floating-blob" />
        <div className="floating-blob" />
        <div className="floating-blob" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 py-3 sm:py-6 px-3 sm:px-4">
        
        {/* Global Success Message */}
        {successMessage && (
          <div className="fixed top-4 right-4 z-50 max-w-md">
            <div className="bg-green-500/90 backdrop-blur-md border border-green-400/50 rounded-2xl p-4 text-white shadow-2xl">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">{successMessage}</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="container mx-auto max-w-7xl">
          
          {/* Header */}
          <div className="mb-4 sm:mb-8">
            <div className={`glass ${darkMode ? 'glass-dark' : ''} rounded-2xl p-6 shadow-2xl`}>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white shadow-lg">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Partners Management
                    </h1>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Manage partner referrals and track student progress
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/admin/partner-registration')}
                    className={`px-4 py-2 rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 ${
                      darkMode 
                        ? 'bg-gray-700 text-white hover:bg-gray-600' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add New Partner
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-4 sm:mb-6">
            <div className={`glass ${darkMode ? 'glass-dark' : ''} rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-xl`}>
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex-1 relative">
                  <svg className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search partners by name, phone, or institution..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border-2 transition-all duration-300 ${
                      darkMode 
                        ? 'bg-gray-800/50 border-gray-700 text-white placeholder-gray-400 focus:border-blue-500' 
                        : 'bg-white/70 border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Partners Count and Sort Indicator */}
          <div className="mb-4 flex items-center justify-between">
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Showing {filteredPartners.length} partner{filteredPartners.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Partners Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {filteredPartners.map((partner) => {
              const partnerReferrals = getPartnerReferrals(partner.id);
              const averageProgress = getPartnerAverageProgress(partner.id);
              return (
                <div
                  key={partner.id}
                  className={`glass ${darkMode ? 'glass-dark' : ''} rounded-xl p-6 shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer relative`}
                  onClick={() => {
                    setSelectedPartner(partner);
                    setShowReferrals(true);
                  }}
                >
                  {/* Action Buttons */}
                  <div className="absolute top-3 right-3 flex gap-2">
                    {/* Edit Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditPartner(partner);
                      }}
                      className="p-2 rounded-full bg-blue-500/20 hover:bg-blue-500/30 text-blue-500 transition-all duration-200 z-10"
                      title="Edit Partner"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    
                    {/* Delete Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPartnerToDelete(partner);
                        setShowDeleteConfirm(true);
                      }}
                      className="p-2 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-500 transition-all duration-200 z-10"
                      title="Delete Partner"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>

                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold">
                        {partner.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {partner.name}
                        </h3>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {partner.phone}
                        </p>
                        <p className={`text-xs font-medium ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          ID: {partner.partnerID || partner.phone}
                        </p>
                        {partner.email && (
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {partner.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {partner.institution && (
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {partner.institution}
                        </span>
                      </div>
                    )}

                    {partner.email && (
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} break-all`}>
                          {partner.email}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {partner.address}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Joined {formatDate(partner.registeredAt)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200/20">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Active Referrals
                        </span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm font-medium text-blue-500">
                            {partnerReferrals.length} students
                          </span>
                          {partnerReferrals.length > 0 && (
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              • Avg {averageProgress}% complete
                            </span>
                          )}
                        </div>
                      </div>
                      {partnerReferrals.length > 0 && (
                        <div className="flex-shrink-0">
                          <CircularProgress percentage={averageProgress} size={50} />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Account Info */}
                  {partner.paymentAccount && (partner.paymentAccount.hasUpi || partner.paymentAccount.hasBank) && (
                    <div className="mt-3 pt-3 border-t border-gray-200/20">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        <div className="flex flex-col">
                          <span className={`text-sm font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                            {partner.paymentAccount.hasUpi && partner.paymentAccount.hasBank 
                              ? 'Both Payment Methods Available:' 
                              : partner.paymentAccount.hasUpi 
                                ? 'UPI Payment Available:' 
                                : 'Bank Details Available:'
                            }
                          </span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {partner.paymentAccount.hasUpi && (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                darkMode 
                                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                                  : 'bg-blue-100 text-blue-700 border border-blue-200'
                              }`}>
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                                UPI Payment
                              </span>
                            )}
                            {partner.paymentAccount.hasBank && (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                darkMode 
                                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                                  : 'bg-purple-100 text-purple-700 border border-purple-200'
                              }`}>
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                Bank Details
                              </span>
                            )}
                            {!partner.paymentAccount.hasUpi && partner.paymentAccount.hasBank && (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                darkMode 
                                  ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' 
                                  : 'bg-orange-100 text-orange-700 border border-orange-200'
                              }`}>
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                UPI Not Provided
                              </span>
                            )}
                            {partner.paymentAccount.hasUpi && !partner.paymentAccount.hasBank && (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                darkMode 
                                  ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' 
                                  : 'bg-orange-100 text-orange-700 border border-orange-200'
                              }`}>
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Bank Details Not Provided
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* No Payment Info */}
                  {(!partner.paymentAccount || (!partner.paymentAccount.hasUpi && !partner.paymentAccount.hasBank)) && (
                    <div className="mt-3 pt-3 border-t border-gray-200/20">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          No payment details provided
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {filteredPartners.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                No partners found matching your search.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Referrals Modal */}
      {showReferrals && selectedPartner && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-1 sm:p-4">
          <div className={`rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-4 md:p-6 max-w-7xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl custom-scrollbar backdrop-blur-xl ${
            darkMode 
              ? 'bg-gray-800/95 border border-gray-700/50' 
              : 'bg-white/95 border border-gray-200/50'
          } mx-1 sm:mx-4`}>
            
            {/* Header Section - Compact Mobile */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-1 sm:mb-3 space-y-1 sm:space-y-0">
              <div className="flex-1 min-w-0 pr-2 sm:pr-4">
                <h2 className={`text-sm sm:text-xl md:text-2xl font-bold break-words ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {selectedPartner.name} - Referrals
                </h2>
                <p className={`text-xs sm:text-sm mt-0 sm:mt-1 break-words leading-tight ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <span className="hidden sm:inline">Manage student referrals and track their progress</span>
                  <span className="sm:hidden">Track progress</span>
                </p>
              </div>
              
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 w-full sm:w-auto">
                {/* Add Student Button */}
                <button
                  onClick={() => {
                    setShowAddStudent(true);
                    // No need to clear warning since we removed it
                  }}
                  className={`flex-1 sm:flex-none px-2 sm:px-3 py-1 sm:py-2 rounded-lg font-medium text-xs transition-all duration-300 hover:scale-105 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg border-0 focus:outline-none focus:ring-2 focus:ring-green-300`}
                >
                  <div className="flex items-center gap-1 justify-center">
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span className="whitespace-nowrap text-xs">Add Student</span>
                  </div>
                </button>
                
                {/* Close Button */}
                <button
                  onClick={() => setShowReferrals(false)}
                  className={`p-1.5 sm:p-2 rounded-lg transition-colors flex-shrink-0 ${
                    darkMode 
                      ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Referrals List - Compact Mobile */}
            <div className="space-y-2 sm:space-y-3">
              {getPartnerReferrals(selectedPartner.id).map((referral) => (
                <div
                  key={referral.id}
                  className={`rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-lg border backdrop-blur-sm transition-all duration-300 hover:shadow-xl ${
                    darkMode 
                      ? 'bg-gray-900/80 border-gray-700/50 hover:bg-gray-900/90' 
                      : 'bg-gray-50/80 border-gray-200/50 hover:bg-gray-50/90'
                  }`}
                >
                  {/* Student Info Header - Compact */}
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-2 sm:mb-3 space-y-1 sm:space-y-0">
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold text-sm sm:text-lg break-words ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {referral.studentName}
                      </h3>
                      <div className={`text-xs space-y-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <p className="break-all">{referral.studentEmail}</p>
                        <p className="break-all">{referral.studentPhone}</p>
                      </div>
                      <p className={`text-xs font-medium mt-0.5 break-words ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {referral.university}
                      </p>
                    </div>
                    
                    {/* Progress and Actions - Compact */}
                    <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 flex-shrink-0">
                      <div className="text-center sm:text-right">
                        <div className="text-sm sm:text-xl font-bold text-blue-500">
                          {getStageProgress(referral.stages)}%
                        </div>
                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Done
                        </div>
                      </div>
                      
                      {/* Delete Button */}
                      <button
                        onClick={() => handleDeleteReferral(referral.id)}
                        className={`p-1.5 sm:p-2 rounded-lg transition-all duration-200 hover:scale-110 ${
                          darkMode 
                            ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20' 
                            : 'text-red-500 hover:text-red-700 hover:bg-red-50'
                        }`}
                        title="Delete Referral"
                      >
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Progress Stages - Compact Square Grid for Mobile */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-1.5 sm:gap-2">
                    {referralStages.map((stage, index) => {
                      // Check if this stage can be clicked (all previous stages must be completed)
                      const canClick = index === 0 || referralStages.slice(0, index).every(prevStage => referral.stages[prevStage.id]);
                      const isCompleted = referral.stages[stage.id];
                      
                      return (
                        <div
                          key={stage.id}
                          className={`relative aspect-square flex flex-col items-center justify-center p-1 sm:p-1.5 rounded-md sm:rounded-lg transition-all duration-200 ${
                            canClick 
                              ? 'cursor-pointer hover:scale-[1.05]' 
                              : 'cursor-not-allowed opacity-50'
                          } ${
                            isCompleted
                              ? 'bg-green-500/20 border-green-500/40 border-2 shadow-green-500/20 shadow-md'
                              : canClick
                                ? 'bg-gray-500/10 border-gray-500/20 border hover:bg-gray-500/20 hover:border-gray-500/30'
                                : 'bg-gray-500/5 border-gray-500/10 border'
                          }`}
                          onClick={() => canClick && handleStageToggle(referral.id, stage.id)}
                        >
                        {/* Status indicator dot */}
                        <div className={`absolute top-0.5 right-0.5 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
                          isCompleted ? 'bg-green-500' : canClick ? 'bg-gray-400' : 'bg-gray-300'
                        }`}></div>
                        
                        {/* Icon */}
                        <div className={`flex-shrink-0 mb-0.5 ${
                          isCompleted 
                            ? 'text-green-500' 
                            : canClick 
                              ? (darkMode ? 'text-gray-400' : 'text-gray-500')
                              : (darkMode ? 'text-gray-500' : 'text-gray-400')
                        }`}>
                          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        
                        {/* Label */}
                        <div className={`text-center ${
                          darkMode ? 'text-gray-300' : 'text-gray-900'
                        }`}>
                          <div className="text-xs font-medium leading-tight">
                            {stage.label.split(' ')[0]}
                          </div>
                          <div className={`text-xs font-medium ${
                            isCompleted 
                              ? 'text-green-500' 
                              : canClick
                                ? (darkMode ? 'text-gray-400' : 'text-gray-500')
                                : (darkMode ? 'text-gray-500' : 'text-gray-400')
                          }`}>
                            {isCompleted ? '✓' : '○'}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>

                  {/* Date and Status Footer - Compact */}
                  <div className={`mt-2 sm:mt-3 pt-2 sm:pt-3 border-t flex flex-col sm:flex-row sm:items-center justify-between space-y-1 sm:space-y-0 ${
                    darkMode ? 'border-gray-700/50' : 'border-gray-200/50'
                  }`}>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span className="font-medium">Ref:</span> {formatDate(referral.referredAt)}
                    </div>
                    <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      getStageProgress(referral.stages) === 100
                        ? 'bg-green-100 text-green-800 border border-green-200'
                        : 'bg-blue-100 text-blue-800 border border-blue-200'
                    }`}>
                      {getStageProgress(referral.stages) === 100 ? 'Done' : 'Active'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add Student Modal - Mobile Responsive */}
      {showAddStudent && selectedPartner && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div className={`rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 max-w-4xl w-full max-h-[95vh] sm:max-h-[85vh] overflow-y-auto shadow-2xl custom-scrollbar backdrop-blur-xl ${
            darkMode 
              ? 'bg-gray-800/95 border border-gray-700/50' 
              : 'bg-white/95 border border-gray-200/50'
          }`}>
            
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
              <div className="flex-1 min-w-0">
                <h2 className={`text-lg sm:text-xl md:text-2xl font-bold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Add Student to {selectedPartner.name}
                </h2>
                <p className={`text-xs sm:text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Select from approved students to add as referral
                </p>
              </div>
              
              <button
                onClick={() => {
                  setShowAddStudent(false);
                  setStudentSearchTerm('');
                  // No need to clear warning since we removed it
                }}
                className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                  darkMode 
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Duplicate Warning */}
            {/* Removed duplicate warning - now just prevent selection instead */}

            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search approved students by name, email, or phone..."
                  value={studentSearchTerm}
                  onChange={(e) => setStudentSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 transition-all duration-300 ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                />
                <svg 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Students List */}
            <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
              {approvedStudents
                .filter(student => 
                  student.name.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
                  student.email.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
                  student.phone.includes(studentSearchTerm) ||
                  student.desiredCollege.toLowerCase().includes(studentSearchTerm.toLowerCase())
                )
                .map((student) => {
                  // Check if this student is already referred to ANY partner (not just current partner)
                  const isAlreadyReferred = referrals.some(ref => ref.studentEmail === student.email);
                  
                  return (
                    <div
                      key={student.id}
                      className={`rounded-xl p-4 border-2 transition-all duration-200 ${
                        isAlreadyReferred 
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-50' 
                          : `cursor-pointer hover:scale-[1.02] ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 hover:border-blue-500 hover:bg-gray-600' 
                                : 'bg-gray-50 border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                            }`
                      }`}
                      onClick={() => !isAlreadyReferred && handleAddStudent(student.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className={`font-semibold text-lg ${
                            isAlreadyReferred 
                              ? 'text-gray-500' 
                              : darkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {student.name}
                            {isAlreadyReferred && (
                              <span className="ml-2 text-sm font-normal text-gray-500">(Already Referred)</span>
                            )}
                          </h3>
                          <div className={`text-sm ${
                            isAlreadyReferred 
                              ? 'text-gray-400' 
                              : darkMode ? 'text-gray-300' : 'text-gray-600'
                          } space-y-1`}>
                            <p>{student.email} | {student.phone}</p>
                            <p className={`font-medium ${
                              isAlreadyReferred 
                                ? 'text-gray-500' 
                                : darkMode ? 'text-blue-400' : 'text-blue-600'
                            }`}>
                              {student.desiredCollege}
                            </p>
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                isAlreadyReferred 
                                  ? 'bg-gray-100 text-gray-600' 
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {student.status}
                              </span>
                              <span className={`text-xs ${
                                isAlreadyReferred 
                                  ? 'text-gray-400' 
                                  : darkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                ID: {student.id}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="ml-4">
                          {isAlreadyReferred ? (
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                            </svg>
                          ) : (
                            <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              
              {/* No students message */}
              {approvedStudents
                .filter(student => 
                  student.name.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
                  student.email.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
                  student.phone.includes(studentSearchTerm) ||
                  student.desiredCollege.toLowerCase().includes(studentSearchTerm.toLowerCase())
                ).length === 0 && (
                <div className="text-center py-8">
                  <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    No students found
                  </p>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {studentSearchTerm ? 'Try adjusting your search terms' : 'No approved students available'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && partnerToDelete && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl p-6 max-w-md w-full shadow-2xl ${
            darkMode 
              ? 'bg-gray-800 border-2 border-gray-700' 
              : 'bg-white border-2 border-gray-200'
          }`}>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.99-.833-2.76 0L3.054 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              
              <div>
                <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Delete Partner
                </h3>
                <p className={`text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Are you sure you want to delete <strong>{partnerToDelete.name}</strong>? 
                  This will also remove all their referrals and cannot be undone.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => handleDeletePartner(partnerToDelete.id)}
                  className="flex-1 py-3 px-4 rounded-xl font-medium text-white transition-all duration-300 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 hover:scale-105 active:scale-95"
                >
                  Delete
                </button>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setPartnerToDelete(null);
                  }}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-300 hover:scale-105 active:scale-95 ${
                    darkMode 
                      ? 'bg-gray-700 text-white hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}      {/* Edit Partner Modal */}
      {showEditPartner && editingPartner && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          {/* Animated Background Orbs */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-blue-300/30 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
            <div className="absolute top-1/3 right-1/4 w-40 h-40 bg-purple-300/30 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
            <div className="absolute bottom-1/4 left-1/3 w-36 h-36 bg-indigo-300/30 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
          </div>

          <div className={`backdrop-blur-xl shadow-2xl max-w-4xl w-full max-h-[92vh] relative flex flex-col rounded-3xl overflow-hidden ${
            darkMode 
              ? 'bg-gray-800/95 border border-gray-700/50' 
              : 'bg-white/95 border border-gray-200/30'
          }`}>
            {/* Subtle inner glow */}
            <div className={`absolute inset-0 rounded-3xl pointer-events-none ${
              darkMode 
                ? 'bg-gradient-to-br from-white/10 via-transparent to-transparent' 
                : 'bg-gradient-to-br from-white/40 via-white/20 to-transparent'
            }`}></div>
            
            {/* Header - Fixed */}
            <div className={`relative border-b p-6 flex-shrink-0 rounded-t-3xl ${
              darkMode 
                ? 'border-gray-700/50 bg-gradient-to-r from-gray-800/80 to-gray-700/60' 
                : 'border-gray-200/40 bg-gradient-to-r from-white/80 to-gray-50/60'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Partner Icon */}
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-md border border-white/30 flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Edit Partner
                    </h2>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {editingPartner.name} • {editingPartner.partnerID || editingPartner.phone}
                    </p>
                  </div>
                </div>
                
                  <button
                  onClick={() => setShowEditPartner(false)}
                  className={`p-3 rounded-2xl transition-all duration-300 hover:scale-110 backdrop-blur-md border ${
                    darkMode 
                      ? 'bg-gray-700/50 text-gray-300 hover:text-white hover:bg-gray-600/70 border-gray-600/30' 
                      : 'bg-white/70 text-gray-600 hover:text-gray-900 hover:bg-white/90 border-gray-300/30'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content with Custom Invisible Scrollbar - Flexible */}
            <div className="relative flex-1 overflow-y-auto scrollbar-hide p-6" style={{
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none' // IE/Edge
            }}>
              <div className="space-y-6">
                {/* Basic Information Section */}
                <div className="space-y-4">
                  <h3 className={`text-lg font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Basic Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        Partner Name *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="partnerName"
                          value={editForm.partnerName}
                          onChange={handleEditFormChange}
                          className={`w-full p-4 rounded-2xl backdrop-blur-md border transition-all duration-300 ${
                            darkMode 
                              ? 'bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-300 focus:bg-gray-700/70 focus:border-gray-500/70' 
                              : 'bg-white/80 border-gray-300/50 text-gray-900 placeholder-gray-500 focus:bg-white/95 focus:border-gray-400/70'
                          } focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                          placeholder="Enter partner name"
                        />
                        <div className={`absolute inset-0 rounded-2xl pointer-events-none ${
                          darkMode 
                            ? 'bg-gradient-to-br from-white/5 to-transparent' 
                            : 'bg-gradient-to-br from-black/5 to-transparent'
                        }`}></div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        Phone Number *
                      </label>
                      <div className="relative">
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={editForm.phoneNumber}
                          onChange={(e) => {
                            // Allow only digits and limit to 10 characters
                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                            handleEditFormChange({ target: { name: 'phoneNumber', value } });
                          }}
                          className={`w-full p-4 rounded-2xl backdrop-blur-md border transition-all duration-300 ${
                            darkMode 
                              ? 'bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-300 focus:bg-gray-700/70 focus:border-gray-500/70' 
                              : 'bg-white/80 border-gray-300/50 text-gray-900 placeholder-gray-500 focus:bg-white/95 focus:border-gray-400/70'
                          } focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                          placeholder="Enter 10-digit phone number"
                          maxLength="10"
                        />
                        <div className={`absolute inset-0 rounded-2xl pointer-events-none ${
                          darkMode 
                            ? 'bg-gradient-to-br from-white/5 to-transparent' 
                            : 'bg-gradient-to-br from-black/5 to-transparent'
                        }`}></div>
                        {editForm.phoneNumber && editForm.phoneNumber.length !== 10 && (
                          <p className="text-red-400 text-xs mt-1">Phone number must be exactly 10 digits</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        Email Address *
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          value={editForm.email}
                          onChange={handleEditFormChange}
                          className={`w-full p-4 rounded-2xl backdrop-blur-md border transition-all duration-300 ${
                            darkMode 
                              ? 'bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-300 focus:bg-gray-700/70 focus:border-gray-500/70' 
                              : 'bg-white/80 border-gray-300/50 text-gray-900 placeholder-gray-500 focus:bg-white/95 focus:border-gray-400/70'
                          } focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                          placeholder="Enter email address"
                        />
                        <div className={`absolute inset-0 rounded-2xl pointer-events-none ${
                          darkMode 
                            ? 'bg-gradient-to-br from-white/5 to-transparent' 
                            : 'bg-gradient-to-br from-black/5 to-transparent'
                        }`}></div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        Institution Name
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="institutionName"
                          value={editForm.institutionName}
                          onChange={handleEditFormChange}
                          className={`w-full p-4 rounded-2xl backdrop-blur-md border transition-all duration-300 ${
                            darkMode 
                              ? 'bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-300 focus:bg-gray-700/70 focus:border-gray-500/70' 
                              : 'bg-white/80 border-gray-300/50 text-gray-900 placeholder-gray-500 focus:bg-white/95 focus:border-gray-400/70'
                          } focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                          placeholder="Enter institution name"
                        />
                        <div className={`absolute inset-0 rounded-2xl pointer-events-none ${
                          darkMode 
                            ? 'bg-gradient-to-br from-white/5 to-transparent' 
                            : 'bg-gradient-to-br from-black/5 to-transparent'
                        }`}></div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Address *
                    </label>
                    <div className="relative">
                      <textarea
                        name="address"
                        value={editForm.address}
                        onChange={handleEditFormChange}
                        rows={3}
                        className={`w-full p-4 rounded-2xl backdrop-blur-md border transition-all duration-300 resize-none ${
                          darkMode 
                            ? 'bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-300 focus:bg-gray-700/70 focus:border-gray-500/70' 
                            : 'bg-white/80 border-gray-300/50 text-gray-900 placeholder-gray-500 focus:bg-white/95 focus:border-gray-400/70'
                        } focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                        placeholder="Enter address"
                      />
                      <div className={`absolute inset-0 rounded-2xl pointer-events-none ${
                        darkMode 
                          ? 'bg-gradient-to-br from-white/5 to-transparent' 
                          : 'bg-gradient-to-br from-black/5 to-transparent'
                      }`}></div>
                    </div>
                  </div>
                </div>

                {/* Payment Account Details Section */}
                <div className="border-t border-white/10 pt-6">
                  <h3 className={`text-lg font-semibold flex items-center gap-2 mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Payment Account Details (Optional)
                  </h3>
                  
                  {/* Payment Type Selection */}
                  <div className="space-y-4 mb-6">
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Select one or both payment methods:
                    </p>
                    
                    {/* UPI Checkbox */}
                    <div className="flex items-center gap-3">
                      <label className="relative flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editForm.paymentAccount.hasUpi}
                          onChange={(e) => handlePaymentTypeChange('hasUpi', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-lg border-2 transition-all duration-300 ${
                          editForm.paymentAccount.hasUpi
                            ? 'bg-blue-500 border-blue-500' 
                            : darkMode 
                              ? 'border-gray-500 hover:border-gray-400' 
                              : 'border-gray-300 hover:border-gray-400'
                        }`}>
                          {editForm.paymentAccount.hasUpi && (
                            <svg className="w-4 h-4 text-white ml-0.5 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className={`ml-3 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          UPI Payment
                        </span>
                      </label>
                    </div>

                    {/* Bank Account Checkbox */}
                    <div className="flex items-center gap-3">
                      <label className="relative flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editForm.paymentAccount.hasBank}
                          onChange={(e) => handlePaymentTypeChange('hasBank', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-lg border-2 transition-all duration-300 ${
                          editForm.paymentAccount.hasBank
                            ? 'bg-blue-500 border-blue-500' 
                            : darkMode 
                              ? 'border-gray-500 hover:border-gray-400' 
                              : 'border-gray-300 hover:border-gray-400'
                        }`}>
                          {editForm.paymentAccount.hasBank && (
                            <svg className="w-4 h-4 text-white ml-0.5 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className={`ml-3 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Bank Account
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* UPI Details */}
                  {editForm.paymentAccount.hasUpi && (
                    <div className="space-y-2">
                      <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        UPI ID
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="paymentAccount.upiId"
                          value={editForm.paymentAccount.upiId}
                          onChange={handleEditFormChange}
                          className={`w-full p-4 rounded-2xl backdrop-blur-md border transition-all duration-300 ${
                            darkMode 
                              ? 'bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-300 focus:bg-gray-700/70 focus:border-gray-500/70' 
                              : 'bg-white/80 border-gray-300/50 text-gray-900 placeholder-gray-500 focus:bg-white/95 focus:border-gray-400/70'
                          } focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                          placeholder="Enter UPI ID (e.g., user@paytm)"
                        />
                        <div className={`absolute inset-0 rounded-2xl pointer-events-none ${
                          darkMode 
                            ? 'bg-gradient-to-br from-white/5 to-transparent' 
                            : 'bg-gradient-to-br from-black/5 to-transparent'
                        }`}></div>
                      </div>
                    </div>
                  )}

                  {/* Bank Account Details */}
                  {editForm.paymentAccount.hasBank && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          Account Holder Name
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="bankAccount.accountHolderName"
                            value={editForm.paymentAccount.bankAccount.accountHolderName}
                            onChange={handleEditFormChange}
                            className={`w-full p-4 rounded-2xl backdrop-blur-md border transition-all duration-300 ${
                              darkMode 
                                ? 'bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-300 focus:bg-gray-700/70 focus:border-gray-500/70' 
                                : 'bg-white/80 border-gray-300/50 text-gray-900 placeholder-gray-500 focus:bg-white/95 focus:border-gray-400/70'
                            } focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                            placeholder="Enter account holder name"
                          />
                          <div className={`absolute inset-0 rounded-2xl pointer-events-none ${
                            darkMode 
                              ? 'bg-gradient-to-br from-white/5 to-transparent' 
                              : 'bg-gradient-to-br from-black/5 to-transparent'
                          }`}></div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          Account Number
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="bankAccount.accountNumber"
                            value={editForm.paymentAccount.bankAccount.accountNumber}
                            onChange={handleEditFormChange}
                            className={`w-full p-4 rounded-2xl backdrop-blur-md border transition-all duration-300 ${
                              darkMode 
                                ? 'bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-300 focus:bg-gray-700/70 focus:border-gray-500/70' 
                                : 'bg-white/80 border-gray-300/50 text-gray-900 placeholder-gray-500 focus:bg-white/95 focus:border-gray-400/70'
                            } focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                            placeholder="Enter account number"
                          />
                          <div className={`absolute inset-0 rounded-2xl pointer-events-none ${
                            darkMode 
                              ? 'bg-gradient-to-br from-white/5 to-transparent' 
                              : 'bg-gradient-to-br from-black/5 to-transparent'
                          }`}></div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          IFSC Code
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="bankAccount.ifscCode"
                            value={editForm.paymentAccount.bankAccount.ifscCode}
                            onChange={handleEditFormChange}
                            className={`w-full p-4 rounded-2xl backdrop-blur-md border transition-all duration-300 ${
                              darkMode 
                                ? 'bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-300 focus:bg-gray-700/70 focus:border-gray-500/70' 
                                : 'bg-white/80 border-gray-300/50 text-gray-900 placeholder-gray-500 focus:bg-white/95 focus:border-gray-400/70'
                            } focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                            placeholder="Enter IFSC code"
                          />
                          <div className={`absolute inset-0 rounded-2xl pointer-events-none ${
                            darkMode 
                              ? 'bg-gradient-to-br from-white/5 to-transparent' 
                              : 'bg-gradient-to-br from-black/5 to-transparent'
                          }`}></div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          Bank Name
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="bankAccount.bankName"
                            value={editForm.paymentAccount.bankAccount.bankName}
                            onChange={handleEditFormChange}
                            className={`w-full p-4 rounded-2xl backdrop-blur-md border transition-all duration-300 ${
                              darkMode 
                                ? 'bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-300 focus:bg-gray-700/70 focus:border-gray-500/70' 
                                : 'bg-white/80 border-gray-300/50 text-gray-900 placeholder-gray-500 focus:bg-white/95 focus:border-gray-400/70'
                            } focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                            placeholder="Enter bank name"
                          />
                          <div className={`absolute inset-0 rounded-2xl pointer-events-none ${
                            darkMode 
                              ? 'bg-gradient-to-br from-white/5 to-transparent' 
                              : 'bg-gradient-to-br from-black/5 to-transparent'
                          }`}></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer with Action Buttons - Fixed */}
            <div className={`relative border-t p-6 flex-shrink-0 rounded-b-3xl ${
              darkMode 
                ? 'border-gray-700/50 bg-gradient-to-r from-gray-800/80 to-gray-700/60' 
                : 'border-gray-200/40 bg-gradient-to-r from-white/80 to-gray-50/60'
            }`}>
              {/* Error/Success Messages */}
              {error && (
                <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-2xl">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}
              
              {successMessage && (
                <div className="mb-4 p-4 bg-green-500/20 border border-green-500/50 rounded-2xl">
                  <p className="text-green-400 text-sm">{successMessage}</p>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  onClick={handleSavePartner}
                  disabled={loading}
                  className="group relative flex-1 py-4 px-6 rounded-2xl font-semibold text-white transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-2xl hover:shadow-blue-500/25 overflow-hidden"
                >
                  {/* Button background glow */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl"></div>
                  
                  {/* Button content */}
                  <div className="relative flex items-center justify-center space-x-3">
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{loading ? 'Saving...' : 'Save Changes'}</span>
                      </>
                    )}
                  </div>
                </button>
                
                <button
                  onClick={() => setShowEditPartner(false)}
                  className={`flex-1 py-4 px-6 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95 backdrop-blur-md border ${
                    darkMode 
                      ? 'bg-gray-700/50 text-white hover:bg-gray-600/70 border-gray-600/50' 
                      : 'bg-white/80 text-gray-700 hover:bg-white/95 border-gray-300/50'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPartnersManagementPage;
